import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import React, { MutableRefObject, useRef, useState } from 'react';

import { CrisBrandPicker } from 'app/components/cris-brand-picker';
import { CrisCategoryPicker } from 'app/components/cris-category-picker';
import { If } from 'app/components/if';
import { RequestStatus } from 'app/components/request-status';
import { Translate } from 'app/components/translate';
import { SeamStatus } from 'app/constants/seam-status';
import { Brand } from 'app/model/cris/brand';
import { makeBlankStore, Store } from 'app/model/cris/store';
import { CrisRequest } from 'app/model/cris-request';
import { dialogService } from 'app/services/dialog.service';
import { ModelWithoutId } from 'app/type/model-with-id';
import { InputOnChangeNormalizer } from 'app/utils/input-on-change.normalizer';

import { StoreAction } from './actions/store-action';

interface CrisActionsProps {
    request: CrisRequest;

    onChange: (request: CrisRequest) => void;

    onSave: (request: CrisRequest, moveNext: boolean) => void;
}

const RADIO_EDIT = 'edit';
const RADIO_CREATE = 'create';

export const CrisActions: React.FunctionComponent<CrisActionsProps> = ({ request, onChange, onSave }) => {
    const requestRef = useRef<CrisRequest>() as MutableRefObject<CrisRequest>;
    requestRef.current = request;

    const createValue = request.brand ? RADIO_EDIT : RADIO_CREATE;
    const isReview = SeamStatus.TXN_CHECK === request.currentPlace;

    const [lastBrand, setLastBrand] = useState<Brand>();

    const requestDataHandler: InputOnChangeNormalizer = new InputOnChangeNormalizer((name, value) =>
        onChange({
            ...requestRef.current,
            [name]: value,
        }),
    );

    const brandDataHandler: InputOnChangeNormalizer = new InputOnChangeNormalizer((name, value) => {
        requestDataHandler.updateValue('brandData', {
            name: '',
            ...requestRef.current.brandData,
            [name]: value,
        });
    });

    const addAction = () => {
        requestDataHandler.updateValue('storeData', [...requestRef.current.storeData, makeBlankStore()]);
    };

    const handleCreateEditChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
        if (RADIO_CREATE === evt.target.value) {
            dialogService.showConfirm(
                {
                    message: '!!!!ATTENTION!!!!All actions will be switched to "New"',
                    confirmText: 'Continue',
                },
                (): void => {
                    setLastBrand(requestRef.current.brand);
                    onChange({
                        ...requestRef.current,
                        brand: undefined,
                    });
                },
            );

            return;
        }

        dialogService.showConfirm(
            {
                message:
                    '!!ATTENTION!!All actions will be switched to "New" review each as either "New" or "Existing" store',
                confirmText: 'Continue',
            },
            () => {
                // restore last brand if there was one
                onChange({
                    ...requestRef.current,
                    brand: lastBrand || { id: '', name: '' },
                });
            },
        );
    };

    const getBrandForm = (): React.ReactNode => {
        const brandData = request.brandData || request.brand;

        return (
            <Box display='flex' flexDirection='column'>
                {createValue === RADIO_EDIT ? (
                    <CrisBrandPicker
                        value={request.brand}
                        up
                        onChange={(evt, name, brand) => {
                            onChange({
                                ...request,
                                brand,
                                brandData: {
                                    name: brand?.name || '',
                                    category: brand?.category,
                                    midCount: brand?.midCount,
                                    locationId: brand?.locationId,
                                },
                            });
                        }}
                    />
                ) : undefined}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        flexDirection: 'row',
                    }}
                    mt={1}
                >
                    <Box flexBasis='15em' flexGrow={1} mr={2}>
                        <TextField
                            label={<Translate tkey='forms:Name' />}
                            fullWidth
                            name='name'
                            onChange={brandDataHandler.onChange}
                            value={brandData?.name.toUpperCase() || ''}
                            className='mb-1'
                        />
                    </Box>
                    <Box flexBasis='15em' flexGrow={1} mr={2}>
                        <CrisCategoryPicker
                            name='category'
                            onChange={brandDataHandler.onChange}
                            value={brandData?.category}
                        />
                    </Box>
                    <Box flexBasis='5em' flexGrow={1} mr={2}>
                        <TextField
                            label={<Translate tkey='forms:MID Count' />}
                            fullWidth
                            name='midCount'
                            onChange={brandDataHandler.onChange}
                            type='number'
                            value={brandData?.midCount || ''}
                            className='mb-1'
                        />
                    </Box>
                    <Box flexBasis='6em' flexGrow={1} mr={2}>
                        <TextField
                            label={<Translate tkey='forms:Location ID' />}
                            fullWidth
                            name='locationId'
                            onChange={brandDataHandler.onChange}
                            type='number'
                            value={brandData?.locationId || ''}
                            className='mb-1'
                        />
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderStoreActions = () =>
        request.storeData.map((store, key) => (
            <StoreAction
                key={key}
                disabled={!isReview}
                brand={requestRef.current.brand || { name: '' }}
                storeData={store}
                onChange={(storeData) => updateStore(key, storeData)}
                onDelete={() => deleteAction(key)}
            />
        ));

    const deleteAction = (deleteIndex: number) => {
        const dataForDelete = requestRef.current.storeData[deleteIndex];
        const newArray = requestRef.current.storeData.filter((value, index) => index !== deleteIndex);

        if (dataForDelete.isDefault && 0 < newArray.length) {
            // if default store deleted set the first one as default
            newArray[0] = {
                ...newArray[0],
                isDefault: true,
            };
        }

        requestDataHandler.updateValue('storeData', newArray);
    };

    const updateStore = (key: number, storeData: ModelWithoutId<Store> | undefined) => {
        if (!storeData) {
            return;
        }

        const data = [...requestRef.current.storeData];
        const currentBrandStores = requestRef.current.brand?.stores || [];

        if (data[key].isDefault === storeData.isDefault) {
            // default did not change, nothing else to do just process the update
            data[key] = storeData;
            requestDataHandler.updateValue('storeData', data);

            return;
        }

        let localDefault = false;
        for (let storeKey = 0; storeKey < data.length; storeKey++) {
            if (data[storeKey].isDefault) {
                data[storeKey] = {
                    ...data[storeKey],
                    isDefault: false,
                };
                localDefault = true;
                break;
            }
        }

        data[key] = storeData;
        requestDataHandler.updateValue('storeData', data);

        if (!localDefault) {
            // if we are hear it meas no default was previosly set on any of the stores in action queue
            // and we are currently setting one of them as default
            // find the default store in the current brand and alert user that it will be unset as default
            for (const store of currentBrandStores) {
                if (store.isDefault && store.id !== storeData.id) {
                    dialogService.showConfirm(
                        {
                            message: `This will replace current default store: "${store.name}"`,
                            confirmText: 'Continue',
                        },
                        undefined,
                        () => {
                            // if user cancelled, revert back the default set
                            const newData = [...data];
                            newData[key] = {
                                ...storeData,
                                isDefault: false,
                            };
                            requestDataHandler.updateValue('storeData', newData);
                        },
                    );
                    break;
                }
            }
        }
    };

    return (
        <Box display='flex' flexDirection='column'>
            <Grid container spacing={3}>
                <Grid item xs={6}>
                    <Card elevation={4} className='mb-2' style={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant='h4'>CRIS Request</Typography>
                            <Typography variant='h6'>
                                Status <RequestStatus status={request.currentPlace} />
                                <If condition={!isReview}>
                                    <small> (data on this page is read only)</small>
                                </If>
                            </Typography>
                            <Typography variant='h6'>Description</Typography>
                            {request.description}
                            <Typography variant='h6'>ETA:</Typography>
                            <strong>
                                {request.estimatedDelivery
                                    ? format(parseISO(request.estimatedDelivery), 'MM/dd/yyyy')
                                    : '(none)'}
                            </strong>
                            <Typography variant='h6'>
                                <Translate tkey='forms:Requested Due Date' />:
                            </Typography>
                            <strong>
                                {request.dueDate ? format(parseISO(request.dueDate), 'MM/dd/yyyy') : '(none)'}
                            </strong>
                            <Typography variant='h6'>
                                <Translate tkey='forms:Requested Element Name' />:
                            </Typography>
                            <Typography variant='h6'>{request.elementName}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6}>
                    <Card elevation={4} className='mb-2' style={{ height: '100%' }}>
                        <CardHeader
                            title='Associated Brand'
                            subheader='Each request needs to associate with a Brand. Select or create one here.'
                            action={
                                <RadioGroup row value={createValue} onChange={handleCreateEditChange}>
                                    <FormControlLabel
                                        value={RADIO_CREATE}
                                        control={<Radio />}
                                        label='New'
                                        disabled={!isReview}
                                    />
                                    <FormControlLabel
                                        value={RADIO_EDIT}
                                        control={<Radio />}
                                        label='Existing'
                                        disabled={!isReview}
                                    />
                                </RadioGroup>
                            }
                        />
                        <CardContent>{getBrandForm()}</CardContent>
                    </Card>
                </Grid>
            </Grid>
            <Box display='flex' flexDirection='row' my={1}>
                <Box flexGrow={1}>
                    <Typography variant='h5'>Actions Queue</Typography>
                </Box>
                <If condition={isReview}>
                    <Button variant='contained' size='small' onClick={addAction}>
                        Add Action
                    </Button>
                </If>
            </Box>
            {renderStoreActions()}
            <If condition={isReview}>
                <Box display='flex' justifyContent='flex-end'>
                    <Button variant='outlined' size='small' color='primary' onClick={() => onSave(request, false)}>
                        Save
                    </Button>
                    &nbsp;
                    <Button variant='contained' size='small' color='primary' onClick={() => onSave(request, true)}>
                        Save & Continue
                    </Button>
                </Box>
            </If>
        </Box>
    );
};
/* eslint max-lines: off */
